<script setup lang="ts">
import { computed, ref, h, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { ROUNDTRIP, OPENJAW, ONEWAY } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useAirPortionStore } from '@/store/AirPortionStore';
import { useTabComposable } from '../useTabComposable';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import CruiseNoteArea from './CruiseNoteArea.vue';
import CruiseFlightForm from './CruiseFlightForm.vue';
import CruiseSelection from './CruiseSelection.vue';
import FlightInformationModal from '@/components/modal/FlightInformationModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import FlightDetailTable from '@/components/common/FlightDetailTable.vue';
import CruiseHotelForm from './CruiseHotelForm.vue';

const props = defineProps<{
  instanceForm?: object;
  advancedForm: object;
  calendarData?: object;
  indexOption: number;
  cruise: any;
  isFlight: boolean;
  isShowError: boolean;
  validate: object;
  isPreHotel: boolean;
  isPostHotel: boolean;
  isTransfer: boolean;
  maxHotelConfig?: number | null;
  maxRoomConfig?: number | null;
  maxOptionConfig?: number | null;
  maxCategoryConfig?: number | null;
  listCabinCategory: any[];
  listFlightClass?: any[];
  minCalendar: any;
  maxCalendar: any;
}>();

const emits = defineEmits<{
  'update:cruise': [value: any];
  'update:isFlight': [value: any];
  'update:isShowError': [value: boolean];
  'update:isPreHotel': [value: boolean];
  'update:isPostHotel': [value: boolean];
  'update:isTransfer': [value: boolean];
  'update:listFlightClass': [value: any[]];
  addRoom: [];
  removeRoom: [];
  addHotel: [];
  removeHotel: [];
  addPostHotel: [value: boolean];
  addPreHotel: [value: boolean];
  addFlight: [value: boolean];
  addTransfer: [value: boolean];
  updateDestination: [value: any];
  updateLineName: [value: any];
  updateShipName: [value: any];
}>();

const cruiseData = computed({
  get() {
    return props.cruise;
  },
  set(value) {
    emits('update:cruise', value);
  }
});

const isShowError = computed({
  get() {
    return props.isShowError;
  },
  set(value) {
    emits('update:isShowError', value);
  }
});

const isPreHotel = computed({
  get() {
    return props.isPreHotel;
  },
  set(value) {
    emits('update:isPreHotel', value);
  }
});

const isPostHotel = computed({
  get() {
    return props.isPostHotel;
  },
  set(value) {
    emits('update:isPostHotel', value);
  }
});

const isFlight = computed({
  get() {
    return props.isFlight;
  },
  set(value) {
    emits('update:isFlight', value);
  }
});

const isTransfer = computed({
  get() {
    return props.isTransfer;
  },
  set(value) {
    emits('update:isTransfer', value);
  }
});

const listFlightClass = computed({
  get() {
    return props.listFlightClass;
  },
  set(value) {
    emits('update:listFlightClass', value);
  }
});

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const { handleRedirectFlight } = useTabComposable();
const submitQuoteStore = useSubmitQuoteStore();
const airManagementStore = useAirManagementStore();
const adminSetupStore = useAdminSetupStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const airPortionStore = useAirPortionStore();
const adminQuoteStore = useAdminQuoteStore();
const { airs: listAirlineStore } = storeToRefs(airManagementStore);
const cruiseMinimumConfig = computed(() => adminSetupStore.minimumConfig?.cruiseConfig);

const flightTypesOption = [{ type: 'Outbound' }, { type: 'Inbound' }];
const isAvancedForm = ref(true);
const advancedValidate = ref();
const listAirline = ref([]);
const payload = ref([]);
const showTableFlight = ref(false);

const getListAirline = () => {
  adminQuoteStore.getAirlines().then((res) => {
    listAirline.value = res;
  });
};
getListAirline();

const renderNameAirline = (code: string) => {
  const airport = listAirlineStore?.value?.find((air) => air.airportCode === code);
  return `${airport?.airportName} (${airport?.airportCode})`;
};

const addPostHotel = (value: boolean) => {
  isPostHotel.value = value;
  emits('addPostHotel', value);
};

const addPreHotel = (value: boolean) => {
  isPreHotel.value = value;
  emits('addPreHotel', value);
};

const addFlight = (value: boolean) => {
  isFlight.value = value;
  showTableFlight.value = false;
  emits('addFlight', value);
};

const addTransfer = (value: boolean) => {
  isTransfer.value = value;
  emits('addTransfer', value);
};

const clearSelectedFlight = () => {
  listFlightClass.value = [];
  showTableFlight.value = false;
};

function createFlightPayloadObject(flight) {
  return {
    gateCode: flight?.gateway?.airportCode,
    destCode: flight?.destination?.airportCode,
    depDate: formatDate(flight?.dateRange[0]),
    retDate: formatDate(flight?.dateRange[1]),
    direct: flight?.directFlight
  };
}

const getListFlight = (listFlight: any) => {
  listFlight[0]?.forEach((flight, fIndex) => {
    if (cruiseData.value.flight[0].tripTypeSelected === ONEWAY) {
      flight.type = 'Outbound';
      listFlightClass.value.push(flight);
    } else {
      flight.type = flightTypesOption[fIndex]?.type;
      listFlightClass.value.push(flight);
    }

    if (flight?.stopFlights?.length) {
      flight?.stopFlights.forEach((sFlight) => {
        if (cruiseData.value.flight[0].tripTypeSelected === ONEWAY) {
          sFlight.type = 'Outbound';
          listFlightClass.value.push(sFlight);
        } else {
          sFlight.type = flightTypesOption[fIndex]?.type;
          listFlightClass.value.push(sFlight);
        }
      });
    }
  });
  listFlightClass.value.forEach((flight) => {
    flight.gate = renderNameAirline(flight.depAirport);
    flight.dest = renderNameAirline(flight.arrivalAirport);
  });
};

const checkPassengerAir = (flights: any) => {
  return flights.some((flight: any) => +flight.adults + +flight.businessAdults + +flight.children + +flight.businessChildren < cruiseMinimumConfig.value.minPax);
};

const openModalValidateAdult = () => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      modal: true,
      header: t('advance_quote.form.notice')
    },
    data: {
      message: t('advance_quote.form.min_pax_air', { minPax: cruiseMinimumConfig.value.minPax })
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.return_to_form'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    }
  });
};

const openFlightInformationModal = () => {
  const dialogRef = dialog.open(FlightInformationModal, {
    data: {
      flightInformation: airPortionStore?.selectFlightInformation
    },
    props: {
      header: t('create_quote.flight_information'),
      modal: true
    },
    emits: {
      isSave: (inbound, outbound, listFlight) => {
        dialogRef.close();
        getListFlight(listFlight);
        showTableFlight.value = true;
      },
      onClose: () => {
        dialogRef.close();
        airPortionStore.clearListFlightInformation();
      }
    },
    onClose: () => {
      airPortionStore.clearListFlightInformation();
    }
  });
};

const handleSearchFlight = async (payload: any, payloadCruiseOpenJaw: any, payloadCruiseOneway: any) => {
  submitQuoteStore.isLoading = true;
  for (let cruiseValue = 0; cruiseValue < cruiseData.value.flight.length; cruiseValue++) {
    if (cruiseData.value.flight[cruiseValue].tripTypeSelected === ROUNDTRIP) {
      await airPortionStore.getFlightInformation([payload[cruiseValue]]);
    }
    if (cruiseData.value.flight[cruiseValue].tripTypeSelected === OPENJAW) {
      await airPortionStore.searchFlightOptionJaw([payloadCruiseOpenJaw[cruiseValue]]);
    }
    if (cruiseData.value.flight[cruiseValue].tripTypeSelected === ONEWAY) {
      await airPortionStore.searchFlightOptionOneWay([payloadCruiseOneway[cruiseValue]]);
    }
  }
  submitQuoteStore.isLoading = false;
};

const openSelectFlightDialog = async () => {
  const payloadCruiseOpenJaw: any = [];
  const payloadCruiseOneway: any = [];
  cruiseData.value.flight.forEach((cruise, index) => {
    const flightPayload = createFlightPayloadObject(cruise);

    if (cruise.tripTypeSelected === ROUNDTRIP) {
      payload.value[index] = flightPayload;
    }
    if (cruise.tripTypeSelected === OPENJAW) {
      payloadCruiseOpenJaw[index] = [
        {
          gateCode: cruise.gateway.airportCode,
          destCode: cruise.destination.airportCode,
          depDate: formatDate(cruise.dateRange[0]),
          direct: cruise.directFlight
        },
        {
          gateCode: cruise.returningFrom.airportCode,
          destCode: cruise.returningTo.airportCode,
          depDate: formatDate(cruise.dateRange[1]),
          direct: cruise.directFlight
        }
      ];
    }
    if (cruise.tripTypeSelected === ONEWAY) {
      payloadCruiseOneway[index] = {
        gateCode: cruise.gateway.airportCode,
        destCode: cruise.destination.airportCode,
        depDate: formatDate(cruise.depDate),
        direct: cruise.directFlight
      };
    }
  });
  clearSelectedFlight();
  advancedValidate.value?.value.$validate();
  isShowError.value = true;
  if (!advancedValidate.value?.value.$error) {
    if (checkPassengerAir(cruiseData.value.flight)) {
      openModalValidateAdult();
    } else {
      await handleSearchFlight(payload.value, payloadCruiseOpenJaw, payloadCruiseOneway);
      if (!submitQuoteStore.isLoading) {
        openFlightInformationModal();
      }
    }
  }
};

const resetDate = () => {
  cruiseData.value.sailDate = null;
  cruiseData.value.retDate = null;
  if (cruiseData.value.preHotel.length) {
    cruiseData.value.preHotel.forEach((pre: any) => {
      pre.dateRange = [];
    });
  }
  if (cruiseData.value.postHotel.length) {
    cruiseData.value.postHotel.forEach((post: any) => {
      post.dateRange = [];
    });
  }
  if (cruiseData.value.flight.length) {
    cruiseData.value.flight.forEach((flightItem: any) => {
      flightItem.depDate = null;
      flightItem.dateRange = [];
    });
  }
};

const addRoom = () => {
  emits('addRoom');
};

const removeRoom = () => {
  emits('removeRoom');
};

const addHotel = () => {
  emits('addHotel');
};

const removeHotel = () => {
  emits('removeHotel');
};

const onUpdateDestination = (value: any) => {
  emits('updateDestination', value);
};

const onUpdateLineName = (value: any) => {
  emits('updateLineName', value);
};

const onUpdateShipName = (value: any) => {
  emits('updateShipName', value);
};

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      resetDate();
    }
  },
  { deep: true }
);
</script>
<template>
  <div class="cruise-form-option">
    <p>{{ $t('common.option') }} {{ indexOption + 1 }}</p>
    <p @click="handleRedirectFlight" class="cursor-pointer flex gap-2 align-items-center justify-content-end"><i class="text-3xl pi pi-exclamation-circle exclamation-icon"></i> {{ $t('common.where_we_fly') }}</p>
    <CruiseSelection
      v-model:cruise="cruiseData"
      :indexOption="indexOption"
      :isShowError="isShowError"
      :advancedForm="advancedForm"
      :validate="validate"
      :minDateValue="minCalendar"
      :maxDateValue="maxCalendar"
      :listCabinCategory="listCabinCategory"
      :maxCategoryConfig="maxCategoryConfig"
      @update-destination="(value) => onUpdateDestination(value)"
      @update-line-name="(value) => onUpdateLineName(value)"
      @update-ship-name="(value) => onUpdateShipName(value)"
    />
    <CruiseNoteArea :type="'diningPreference'" v-model:cruise="cruiseData" :labelNote="t('advance_quote.form.dining_preference')" />
    <CruiseNoteArea :type="'additionalNote'" v-model:cruise="cruiseData" :labelNote="t('advance_quote.form.additional_note')" :permission="authorizations?.advance_quote?.additional_note_view" />
    <div v-if="isAvancedForm">
      <div class="mb-2">
        <Checkbox :inputId="`pre-hotel-${indexOption}`" @update:modelValue="addPreHotel($event)" class="mr-2" :binary="true" v-model="isPreHotel" />
        <label :for="`pre-hotel-${indexOption}`">{{ $t('create_quote.add_pre_hotel') }}</label>
      </div>
      <div class="mb-2">
        <Checkbox :inputId="`post-hotel-${indexOption}`" @update:modelValue="addPostHotel($event)" class="mr-2" :binary="true" v-model="isPostHotel" />
        <label :for="`post-hotel-${indexOption}`">{{ $t('create_quote.add_post_hotel') }}</label>
      </div>
      <div class="mb-2">
        <Checkbox :inputId="`flight-${indexOption}`" @update:modelValue="addFlight($event)" class="mr-2" :binary="true" v-model="isFlight" />
        <label :for="`flight-${indexOption}`">{{ $t('create_quote.add_flight') }}</label>
      </div>
      <div class="mb-2">
        <Checkbox :inputId="`transfers-${indexOption}`" class="mr-2" @update:modelValue="addTransfer($event)" :binary="true" v-model="isTransfer" />
        <label :for="`transfers-${indexOption}`">{{ $t('create_quote.add_transfers') }}</label>
      </div>
      <CruiseHotelForm
        v-if="isPreHotel"
        type="pre"
        v-model:hotels="cruiseData.preHotel"
        :cruiseIndexOption="indexOption"
        :adults="cruiseData.adults"
        :minDateRangeValue="minCalendar"
        :maxDateRangeValue="maxCalendar"
        :isShowError="isShowError"
        :advancedForm="advancedForm"
        :instanceForm="instanceForm"
        :isAvancedForm="isAvancedForm"
        :validate="validate"
        :calendarData="calendarData"
        :maxHotelConfig="maxHotelConfig"
        :maxRoomConfig="maxRoomConfig"
        @addRoom="() => addRoom()"
        @removeRoom="() => removeRoom()"
        @addHotel="() => addHotel()"
        @removeHotel="() => removeHotel()"
      />
      <CruiseHotelForm
        v-if="isPostHotel"
        type="post"
        v-model:hotels="cruiseData.postHotel"
        :cruiseIndexOption="indexOption"
        :adults="cruiseData.adults"
        :minDateRangeValue="minCalendar"
        :maxDateRangeValue="maxCalendar"
        :isShowError="isShowError"
        :advancedForm="advancedForm"
        :instanceForm="instanceForm"
        :isAvancedForm="isAvancedForm"
        :validate="validate"
        :max-hotel-config="maxHotelConfig"
        :max-room-config="maxRoomConfig"
        @addRoom="() => addRoom()"
        @removeRoom="() => removeRoom()"
        @addHotel="() => addHotel()"
        @removeHotel="() => removeHotel()"
      />
      <div :id="`cruise-flight-form-${indexOption}`" v-if="isFlight">
        <div class="m-2 text-3xl font-bold uppercase mt-4">{{ $t('common.flight') }}</div>
        <div class="mb-3">
          <div v-if="!showTableFlight">
            <div class="mb-3">{{ $t('advance_quote.form.air_desc') }}</div>
            <span class="text-primary font-bold">{{ $t('advance_quote.sign') }}</span>
          </div>
          <div v-else>
            <div class="mb-3">{{ $t('management.cruise.selected_flights') }}</div>
          </div>
        </div>
        <div v-if="!showTableFlight">
          <div v-for="(flight, index) in cruiseData.flight" :key="index">
            <CruiseFlightForm
              v-model:isShowError="isShowError"
              :cruiseFlight="flight"
              :validate="validate"
              :minDateRangeValue="minCalendar"
              :maxDateRangeValue="maxCalendar"
              :isFlight="isFlight"
              @clearSelectedFlight="clearSelectedFlight()"
              @openFlightInformationModal="openFlightInformationModal()"
              @openModalValidateAdult="openModalValidateAdult()"
            />
          </div>
        </div>
        <FlightDetailTable v-else :fligtDetail="listFlightClass" @changeFlight="openSelectFlightDialog()" @clearFlight="clearSelectedFlight()"></FlightDetailTable>
      </div>

      <div :id="`cruise-transfer-form-${indexOption}`" v-if="isTransfer">
        <CruiseNoteArea :type="'transfers'" :cruise="cruiseData" :labelNote="t('create_quote.transfers')" :isShowError="isShowError" />
      </div>
    </div>
  </div>
</template>
